import { useState, useCallback } from "react";
import { address as toAddress } from "@solana/kit";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  Loader2,
  Clipboard,
  ExternalLink,
} from "lucide-react";
import { useTranslation } from "@/i18n";
import { useSolanaSendTransaction } from "@/hooks/useSolanaSendTransaction";
import { getExplorerTransactionUrl, shortenAddress } from "@/lib/solana/format";
import { useBrowserWalletStore } from "@nullaone/wallet-react";

type TokenId = "SOL" | "USDC" | "USDT";
type Step = "form" | "confirming" | "success" | "error";

type TokenColor = "emerald" | "blue" | "teal";

interface TokenInfo {
  id: TokenId;
  symbol: string;
  balance: number;
  decimals: number;
  mint?: string;
  color: TokenColor;
}

export interface TransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultToken?: TokenId;
  walletAddress: string;
  solBalance: number | null;
  usdcBalance: number;
  usdcDecimals: number;
  usdcMint?: string;
  usdtBalance: number;
  usdtDecimals: number;
  usdtMint?: string;
  onTransferComplete?: () => void;
}

export function TransferDialog({
  open,
  onOpenChange,
  defaultToken = "SOL",
  walletAddress,
  solBalance,
  usdcBalance,
  usdcDecimals,
  usdcMint,
  usdtBalance,
  usdtDecimals,
  usdtMint,
  onTransferComplete,
}: TransferDialogProps) {
  const { t } = useTranslation();
  const { sendSOL, sendToken } = useSolanaSendTransaction();
  const network = useBrowserWalletStore((state) => state.network);

  const [selectedToken, setSelectedToken] = useState<TokenId>(defaultToken);
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState<Step>("form");
  const [txSignature, setTxSignature] = useState("");
  const [txConfirmed, setTxConfirmed] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const tokens: TokenInfo[] = [
    {
      id: "SOL",
      symbol: "SOL",
      balance: solBalance ?? 0,
      decimals: 9,
      color: "emerald",
    },
    {
      id: "USDC",
      symbol: "USDC",
      balance: usdcBalance,
      decimals: usdcDecimals,
      mint: usdcMint,
      color: "blue",
    },
    {
      id: "USDT",
      symbol: "USDT",
      balance: usdtBalance,
      decimals: usdtDecimals,
      mint: usdtMint,
      color: "teal",
    },
  ];

  // Always guaranteed since selectedToken matches one of the three token ids
  const currentToken = tokens.find(
    (tk) => tk.id === selectedToken,
  ) as TokenInfo;

  const getMaxAmount = (): number => {
    if (selectedToken === "SOL") {
      return Math.max(0, (solBalance ?? 0) - 0.01);
    }
    return currentToken.balance;
  };

  const validateAddress = (addr: string): string | null => {
    if (!addr.trim()) return null;
    try {
      // Kit's `address()` brand validator throws on malformed base58.
      toAddress(addr);
    } catch {
      return t("wallet.invalidAddress");
    }
    if (addr === walletAddress) {
      return t("wallet.selfTransferError");
    }
    return null;
  };

  const validateAmount = (val: string): string | null => {
    const num = parseFloat(val);
    if (!val || isNaN(num) || num <= 0) return null;
    if (num > getMaxAmount()) {
      return t("wallet.insufficientBalance");
    }
    return null;
  };

  const isFormValid = (): boolean => {
    const num = parseFloat(amount);
    if (!recipient.trim() || !amount || isNaN(num) || num <= 0) return false;
    if (validateAddress(recipient)) return false;
    if (num > getMaxAmount()) return false;
    return true;
  };

  const handleSend = useCallback(async () => {
    if (!isFormValid()) return;

    setStep("confirming");
    setErrorMessage("");

    try {
      const parsedAmount = parseFloat(amount);
      let result;

      if (selectedToken === "SOL") {
        result = await sendSOL(recipient, parsedAmount);
      } else {
        const mint = currentToken.mint;
        if (!mint) throw new Error("Token mint not available");
        result = await sendToken(
          recipient,
          mint,
          parsedAmount,
          currentToken.decimals,
          currentToken.symbol,
        );
      }

      if (result.signature && !result.error) {
        // Two sub-cases collapse into one success step (with different copy):
        //   - confirmed=true  → "Transfer successful"
        //   - confirmed=false → "Submitted — finalising on-chain"
        // The relay returns confirmed=false when its ~5s poll window elapses
        // before the cluster reports confirmation; the tx has been broadcast
        // and will land. Surfacing this as an error contradicts the on-chain
        // truth and is the bug this branch fixes.
        setTxSignature(result.signature);
        setTxConfirmed(result.confirmed);
        setStep("success");
        onTransferComplete?.();
      } else if (result.error === "UserRejected") {
        // User cancelled at the pre-sign dialog. The hook already returned
        // silently — bring the dialog back to the form so they can adjust or
        // close it, without showing an error screen.
        setStep("form");
      } else {
        setErrorMessage(result.message || "Transaction failed");
        setStep("error");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setErrorMessage(msg);
      setStep("error");
    }
  }, [
    amount,
    recipient,
    selectedToken,
    currentToken,
    sendSOL,
    sendToken,
    onTransferComplete,
  ]);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setRecipient(text.trim());
    } catch {
      // Clipboard access denied
    }
  };

  const handleMax = () => {
    const max = getMaxAmount();
    if (max > 0) {
      setAmount(
        selectedToken === "SOL"
          ? max.toFixed(4)
          : max.toFixed(currentToken.decimals <= 6 ? currentToken.decimals : 4),
      );
    }
  };

  const handleReset = () => {
    setStep("form");
    setRecipient("");
    setAmount("");
    setTxSignature("");
    setTxConfirmed(true);
    setErrorMessage("");
    setSelectedToken(defaultToken);
  };

  const handleClose = (openState: boolean) => {
    if (!openState) {
      handleReset();
    }
    onOpenChange(openState);
  };

  const addressError = recipient ? validateAddress(recipient) : null;
  const amountError = amount ? validateAmount(amount) : null;

  const tokenColors: Record<TokenColor, { selected: string; dot: string }> = {
    // SOL keeps its brand-accent (gold = "valuable native"); USDC stays
    // brand-primary (blue = money rail). USDT shares the stablecoin
    // semantic but uses success green to keep it visually distinct from
    // USDC at a glance (avoids two blue chips that read as the same coin).
    emerald: {
      selected: "bg-brand-accent/10 border-brand-accent/40 text-brand-accent",
      dot: "bg-brand-accent",
    },
    blue: {
      selected:
        "bg-brand-primary/10 border-brand-primary/40 text-brand-primary",
      dot: "bg-brand-primary",
    },
    teal: {
      selected: "bg-success/10 border-success/40 text-success",
      dot: "bg-success",
    },
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[420px] bg-white border border-border-card text-text-dark p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-lg font-semibold text-text-dark flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-brand-primary/10">
              <ArrowUpRight className="h-4 w-4 text-brand-primary" />
            </div>
            {t("wallet.transfer")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("wallet.transfer")}
          </DialogDescription>
        </DialogHeader>

        {/* Step: Form */}
        {step === "form" && (
          <div className="px-6 pb-6 space-y-5">
            {/* Token selector */}
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-text-muted uppercase tracking-[0.14em]">
                {t("wallet.selectToken")}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {tokens.map((token) => {
                  const colors = tokenColors[token.color];
                  const isSelected = selectedToken === token.id;
                  return (
                    <button
                      key={token.id}
                      onClick={() => setSelectedToken(token.id)}
                      className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl border transition-all ${
                        isSelected
                          ? colors.selected
                          : "bg-bg-page border-border-card text-text-secondary hover:border-text-muted hover:text-text-dark"
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <div className={`h-2 w-2 rounded-full ${colors.dot}`} />
                        <span className="text-sm font-semibold">
                          {token.symbol}
                        </span>
                      </div>
                      <span className="text-[11px] text-text-muted font-mono tabular-nums">
                        {token.balance.toFixed(
                          token.decimals <= 6 ? Math.min(token.decimals, 4) : 4,
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Recipient */}
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-text-muted uppercase tracking-[0.14em]">
                {t("wallet.recipientAddress")}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder={t("wallet.recipientPlaceholder")}
                  className={`w-full bg-bg-page border rounded-xl px-4 py-3 pr-16 text-sm font-mono tabular-nums text-text-dark placeholder:text-text-muted outline-none transition-colors ${
                    addressError
                      ? "border-danger/50 focus:border-danger"
                      : "border-border-card focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/30"
                  }`}
                />
                <button
                  type="button"
                  onClick={handlePaste}
                  className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-text-secondary hover:text-text-dark hover:bg-gray-100 transition-colors"
                >
                  <Clipboard className="h-3 w-3" />
                  {t("wallet.pasteAddress")}
                </button>
              </div>
              {addressError && (
                <p className="text-xs text-danger">{addressError}</p>
              )}
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-bold text-text-muted uppercase tracking-[0.14em]">
                  {t("wallet.amount")}
                </label>
                <span className="text-xs text-text-muted">
                  {t("wallet.available")}:{" "}
                  <span className="font-mono tabular-nums text-text-secondary font-semibold">
                    {getMaxAmount().toFixed(4)}
                  </span>{" "}
                  {currentToken.symbol}
                </span>
              </div>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="any"
                  className={`w-full bg-bg-page border rounded-xl px-4 py-3 pr-20 text-sm font-mono tabular-nums text-text-dark placeholder:text-text-muted outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                    amountError
                      ? "border-danger/50 focus:border-danger"
                      : "border-border-card focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/30"
                  }`}
                />
                <button
                  type="button"
                  onClick={handleMax}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-lg text-xs font-semibold text-brand-primary hover:text-brand-primary-light hover:bg-brand-primary/10 transition-colors"
                >
                  {t("wallet.maxAmount")}
                </button>
              </div>
              {amountError && (
                <p className="text-xs text-danger">{amountError}</p>
              )}
              {selectedToken === "SOL" && !amountError && (
                <p className="text-xs text-text-muted">
                  {t("wallet.solReserveHint")}
                </p>
              )}
            </div>

            {/* Send — brand-primary gradient, matches BrandButton primary */}
            <button
              onClick={handleSend}
              disabled={!isFormValid()}
              className="w-full py-3 rounded-xl text-sm font-bold transition-all bg-gradient-to-r from-brand-primary to-brand-primary-light hover:to-blue-400 text-white shadow-lg shadow-brand-primary/25 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            >
              {t("wallet.send")}{" "}
              {amount && parseFloat(amount) > 0 ? `${amount} ` : ""}
              {currentToken.symbol}
            </button>
          </div>
        )}

        {/* Step: Confirming */}
        {step === "confirming" && (
          <div className="px-6 pb-8 pt-4 flex flex-col items-center gap-4">
            <div className="p-4 rounded-full bg-brand-primary/10">
              <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
            </div>
            <p className="text-sm text-text-secondary text-center">
              {t("wallet.confirmInWallet")}
            </p>
          </div>
        )}

        {/* Step: Success — covers both fully-confirmed and broadcast-pending
            outcomes. Visual cue stays positive (check icon) because the tx
            has been broadcast either way; copy below distinguishes the two. */}
        {step === "success" && (
          <div className="px-6 pb-6 pt-4 flex flex-col items-center gap-4">
            <div
              className={
                txConfirmed
                  ? "p-4 rounded-full bg-success/10 border border-success/30 animate-in zoom-in-50 duration-300"
                  : "p-4 rounded-full bg-brand-primary/10 border border-brand-primary/30 animate-in zoom-in-50 duration-300"
              }
            >
              {txConfirmed ? (
                <CheckCircle2 className="h-8 w-8 text-success" />
              ) : (
                <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
              )}
            </div>
            <p className="text-sm font-semibold text-text-dark">
              {txConfirmed
                ? t("wallet.transferSuccess")
                : t("wallet.transferSubmitted")}
            </p>
            {!txConfirmed && (
              <p className="text-xs text-text-secondary text-center max-w-[300px]">
                {t("wallet.transferSubmittedDesc")}
              </p>
            )}
            {txSignature && (
              <button
                onClick={() => {
                  const url = getExplorerTransactionUrl(txSignature, network);
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
                className="flex items-center gap-2 text-xs text-brand-primary hover:text-brand-primary-light font-mono tabular-nums transition-colors"
              >
                {shortenAddress(txSignature, 8)}
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={() => handleClose(false)}
              className="w-full py-2.5 rounded-xl text-sm font-medium bg-gray-100 hover:bg-gray-200 text-text-dark transition-colors"
            >
              {t("wallet.done")}
            </button>
          </div>
        )}

        {/* Step: Error */}
        {step === "error" && (
          <div className="px-6 pb-6 pt-4 flex flex-col items-center gap-4">
            <div className="p-4 rounded-full bg-danger/10 border border-danger/30 animate-in zoom-in-50 duration-300">
              <XCircle className="h-8 w-8 text-danger" />
            </div>
            <p className="text-sm text-danger text-center max-w-[280px]">
              {errorMessage}
            </p>
            <button
              onClick={() => setStep("form")}
              className="w-full py-2.5 rounded-xl text-sm font-medium bg-gray-100 hover:bg-gray-200 text-text-dark transition-colors"
            >
              {t("wallet.tryAgain")}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
